import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, CheckCircle, Save } from 'lucide-react';

const COLORS = {
  navyBlue: '#003366', orange: '#FF6B35', mustard: '#FFAA00', green: '#16A34A',
  lightGray: '#F9FAFB', white: '#FFFFFF', borderGray: '#D1D5DB', textGray: '#6B7280', textDark: '#1F2937',
};

const CATEGORY_ORDER = ['dyslexia', 'dyspraxia', 'adhd', 'asd', 'dyscalculia', 'sli'];
const RESPONSE_OPTIONS = ['Not at all', 'Sometimes', 'Often'];

const fetchWithTimeout = (url, options = {}, timeoutMs = 30000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const token = window.sessionStorage.getItem('nwaAccessToken');
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { ...options, headers, signal: controller.signal }).finally(() => clearTimeout(timer));
};

const todayDDMMYYYY = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const parseDate = (value) => {
  if (!value) return null;
  const s = String(value).trim();
  let y; let m; let d;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) [y, m, d] = s.split('-').map(Number);
  else if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) [d, m, y] = s.split('/').map(Number);
  else if (/^\d{2}-\d{2}-\d{4}$/.test(s)) [d, m, y] = s.split('-').map(Number);
  else return null;
  const dt = new Date(y, m - 1, d);
  return (dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d) ? dt : null;
};

const ageYearsAt = (dobValue, screenValue) => {
  const dob = parseDate(dobValue); const screen = parseDate(screenValue);
  if (!dob || !screen || screen < dob) return null;
  let years = screen.getFullYear() - dob.getFullYear();
  let months = screen.getMonth() - dob.getMonth();
  let days = screen.getDate() - dob.getDate();
  if (days < 0) months -= 1;
  if (months < 0) { years -= 1; months += 12; }
  return years + (months / 12);
};

const ageBandKeyFor = (ageYears) => {
  if (ageYears == null || ageYears < 4) return null;
  if (ageYears < 6) return '4-6';
  if (ageYears < 12) return '6-11';
  if (ageYears < 16) return '12-16';
  return 'post16';
};

const ageBandDisplay = (key) => ({
  '4-6': '4-6',
  '6-11': '6-11',
  '12-16': '12-16',
  post16: '16+',
}[key] || '');

const styles = {
  container: { minHeight: '100vh', backgroundColor: COLORS.lightGray },
  header: { backgroundColor: COLORS.navyBlue, color: COLORS.white, padding: '8px 16px', borderBottom: `2px solid ${COLORS.mustard}` },
  headerContent: { maxWidth: '1100px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  backButton: { backgroundColor: 'transparent', border: 'none', color: COLORS.white, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', padding: '8px 0' },
  body: { maxWidth: '1100px', margin: '0 auto', padding: '24px' },
  card: { backgroundColor: COLORS.white, borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', padding: '24px', marginBottom: '20px' },
  label: { display: 'block', fontSize: '14px', fontWeight: '600', color: COLORS.textDark, marginBottom: '6px' },
  input: { width: '100%', padding: '10px 14px', border: `2px solid ${COLORS.borderGray}`, borderRadius: '6px', fontSize: '15px', boxSizing: 'border-box' },
  select: { width: '100%', padding: '10px 14px', border: `2px solid ${COLORS.borderGray}`, borderRadius: '6px', fontSize: '15px', backgroundColor: COLORS.white, boxSizing: 'border-box' },
  button: { backgroundColor: COLORS.orange, color: COLORS.white, padding: '12px 24px', borderRadius: '6px', fontWeight: '600', border: 'none', cursor: 'pointer', fontSize: '16px' },
  errorBox: { backgroundColor: '#FEE2E2', border: '1px solid #FCA5A5', color: '#991B1B', padding: '12px 16px', borderRadius: '6px', marginBottom: '16px' },
};

const riskStyle = (band) => {
  if (band === 'High risk') return { color: '#B91C1C', backgroundColor: '#FEE2E2', border: '#FCA5A5' };
  if (band === 'Moderate risk') return { color: '#C2410C', backgroundColor: '#FFF7ED', border: '#FDBA74' };
  return { color: '#15803D', backgroundColor: '#F0FDF4', border: '#86EFAC' };
};

export default function NeurodiversityScreening({ student, apiUrl, facilitatorId, facilitatorName, schoolId, schoolName, programmeId, programmeName, initialTimepoint, onBack, onSaved }) {
  const [config, setConfig] = useState(null);
  const [responses, setResponses] = useState({});
  const [screeningDate, setScreeningDate] = useState(todayDDMMYYYY());
  const [administrationMode, setAdministrationMode] = useState('Individual');
  const administrationBatchId = ''; // kept blank internally; no practitioner entry needed
  const [assessorName, setAssessorName] = useState(facilitatorName || '');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [stage, setStage] = useState('form');
  const timepoint = initialTimepoint || 'Baseline';

  useEffect(() => {
    let active = true;
    fetchWithTimeout(`${apiUrl}/api/neurodiversity/config`)
      .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (!active) return;
        if (!ok) throw new Error(data.error || 'Could not load Neurodiversity Checklist configuration');
        setConfig(data);
      })
      .catch((e) => { if (active) setError(e.message || 'Could not load Neurodiversity Checklist configuration'); });
    return () => { active = false; };
  }, [apiUrl]);

  const ageYears = useMemo(() => ageYearsAt(student?.dob, screeningDate), [student?.dob, screeningDate]);
  const ageBandKey = useMemo(() => ageBandKeyFor(ageYears), [ageYears]);
  const ageBand = config?.age_bands?.[ageBandKey] || null;

  useEffect(() => { setResponses({}); }, [ageBandKey]);

  const classify = (percent) => {
    const bands = config?.classification?.bands || [];
    for (const band of bands) {
      const lower = Number(band.min_percent || 0); const upper = band.max_percent_exclusive;
      if (percent >= lower && (upper == null || percent < Number(upper))) return band.label;
    }
    return 'No risk';
  };

  const profile = useMemo(() => {
    const base = {};
    CATEGORY_ORDER.forEach((key) => { base[key] = { raw: 0, answered: 0, count: Number(ageBand?.item_counts?.[key] || 0), percent: 0, riskBand: 'No risk' }; });
    if (!config || !ageBand) return base;
    const scoreMap = config.response_scoring || {};
    (ageBand.items || []).forEach((item) => {
      const response = responses[item.item_code];
      if (response && scoreMap[response] != null) {
        base[item.category].raw += Number(scoreMap[response]);
        base[item.category].answered += 1;
      }
    });
    CATEGORY_ORDER.forEach((key) => {
      const max = base[key].count * 2;
      base[key].percent = max ? Math.round((base[key].raw / max) * 1000) / 10 : 0;
      base[key].riskBand = classify(base[key].percent);
      base[key].max = max;
    });
    return base;
  }, [config, ageBand, responses]); // eslint-disable-line react-hooks/exhaustive-deps

  const items = ageBand?.items || [];
  const answeredCount = items.filter((item) => !!responses[item.item_code]).length;
  const unansweredCount = Math.max(0, items.length - answeredCount);
  const readyToSave = !!ageBand && items.length > 0 && unansweredCount === 0;

  const unansweredByCategory = useMemo(() => {
    const missing = {};
    CATEGORY_ORDER.forEach((key) => { missing[key] = 0; });
    items.forEach((item) => {
      if (!responses[item.item_code] && missing[item.category] != null) missing[item.category] += 1;
    });
    return missing;
  }, [items, responses]);

  const groupedItems = useMemo(() => {
    const groups = {};
    CATEGORY_ORDER.forEach((key) => { groups[key] = []; });
    items.forEach((item) => { if (groups[item.category]) groups[item.category].push(item); });
    return groups;
  }, [items]);

  const submit = async () => {
    if (!ageBand) { setError('No supplied Neurodiversity Checklist is available for this age on the screening date.'); return; }
    if (!readyToSave) {
      const sections = CATEGORY_ORDER
        .filter((key) => unansweredByCategory[key] > 0)
        .map((key) => `${config?.categories?.[key] || key}: ${unansweredByCategory[key]}`)
        .join('; ');
      setError(`Please complete all checklist statements before saving (${unansweredCount} unanswered). ${sections}`);
      return;
    }
    setError(''); setLoading(true);
    try {
      const res = await fetchWithTimeout(`${apiUrl}/api/neurodiversity/save`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_code: student.student_code, screening_date: screeningDate, timepoint, responses,
          school_id: schoolId || student?.school_id || '', school_name: schoolName || '',
          programme_id: programmeId || student?.programme_id || '', programme_name: programmeName || '',
          assessor_name: assessorName, administration_mode: administrationMode, administration_batch_id: administrationBatchId,
          entered_by_user_id: facilitatorId, entered_by_name: facilitatorName, notes,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not save this Neurodiversity Checklist');
      setResult(data); setStage('complete'); if (onSaved) onSaved(data);
    } catch (e) { setError(e.message || 'Could not save this Neurodiversity Checklist'); }
    finally { setLoading(false); }
  };

  const Header = ({ title }) => (
    <div style={styles.header}><div style={styles.headerContent}>
      <button style={styles.backButton} onClick={onBack}><ArrowLeft size={18} /> Back to {student.full_name}</button>
      <span style={{ fontSize: '13px', color: COLORS.mustard, fontWeight: '600' }}>{title}</span>
    </div></div>
  );

  if (stage === 'complete') {
    const scoring = result?.scoring;
    return <div style={styles.container}><Header title="Neurodiversity Checklist: Saved" /><div style={styles.body}><div style={styles.card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: COLORS.green, marginBottom: '16px' }}><CheckCircle size={28} /><h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>Saved</h2></div>
      {scoring && <>
        <div style={{ backgroundColor: '#F8FAFC', border: `1px solid ${COLORS.borderGray}`, borderRadius: '8px', padding: '14px 16px', margin: '16px 0' }}>
          <div style={{ fontSize: '13px', color: COLORS.textGray }}>{scoring.form_title}</div>
          <div style={{ fontSize: '15px', fontWeight: '700', color: COLORS.navyBlue, marginTop: '4px' }}>{scoring.profile_summary}</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px', marginBottom: '18px' }}>
          {CATEGORY_ORDER.map((key) => { const c = scoring.categories?.[key]; if (!c) return null; const rs = riskStyle(c.risk_band); return <div key={key} style={{ border: `1px solid ${rs.border}`, backgroundColor: rs.backgroundColor, borderRadius: '8px', padding: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: COLORS.textDark }}>{c.label}</div><div style={{ fontSize: '19px', fontWeight: '700', color: rs.color }}>{c.percent}% - {c.risk_band}</div><div style={{ fontSize: '12px', color: COLORS.textGray }}>Raw {c.raw_score}/{c.max_possible_score}</div>
          </div>; })}
        </div>
      </>}
      <button style={{ ...styles.button, backgroundColor: COLORS.white, color: COLORS.navyBlue, border: `2px solid ${COLORS.navyBlue}` }} onClick={onBack}>Back to {student.full_name}</button>
    </div></div></div>;
  }

  return <div style={styles.container}><Header title="Neurodiversity Checklist" /><div style={styles.body}>
    <div style={styles.card}>
      <h1 style={{ fontSize: '22px', fontWeight: 'bold', color: COLORS.navyBlue, margin: '0 0 6px' }}>Neurodiversity Checklist - {student.full_name}</h1>
      <p style={{ color: COLORS.textGray, margin: '0 0 12px' }}>Timepoint: {timepoint}</p>
      <p style={{ fontSize: '14px', lineHeight: 1.55, color: COLORS.textDark, margin: '0 0 10px' }}>This checklist is a first step in identifying learning needs and is <strong>not a diagnostic tool</strong>. For each behaviour select <strong>Not at all</strong>, <strong>Sometimes</strong>, or <strong>Often</strong>.</p>
      <p style={{ fontSize: '13px', color: COLORS.textGray, lineHeight: 1.5, margin: '0 0 16px' }}>Scoring: Not at all = 0, Sometimes = 1, Often = 2. NWA relative-risk bands: No risk &lt;25%, Moderate risk 25-&lt;50%, High risk 50%+. The High risk boundary preserves the previous NWA Stage 1 50% screening threshold.</p>
      {error && <div style={styles.errorBox}>{error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '14px' }}>
        <div><label style={styles.label}>Screening date (DD/MM/YYYY)</label><input style={styles.input} value={screeningDate} onChange={(e) => setScreeningDate(e.target.value)} /></div>
        <div><label style={styles.label}>Checklist selected by age</label><div style={{ ...styles.input, backgroundColor: '#F8FAFC', color: ageBand ? COLORS.textDark : '#B91C1C', minHeight: '43px', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: ageBand ? '600' : '400' }}>{ageBand ? <><span>{ageBandDisplay(ageBandKey)}</span><CheckCircle size={18} color={COLORS.green} aria-label="Correct age-related checklist selected" /></> : 'No supplied checklist available for this age'}</div></div>
        <div><label style={styles.label}>Administration mode</label><select style={styles.select} value={administrationMode} onChange={(e) => setAdministrationMode(e.target.value)}><option value="Individual">Individual</option><option value="Group">Group</option></select></div>
        <div><label style={styles.label}>Assessor name</label><input style={styles.input} value={assessorName} onChange={(e) => setAssessorName(e.target.value)} /></div>
      </div>
    </div>

    {ageBand && <>
      <div style={{ ...styles.card, padding: '16px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}><div><strong style={{ color: COLORS.navyBlue, display: 'flex', alignItems: 'center', gap: '6px' }}>{ageBandDisplay(ageBandKey)} checklist <CheckCircle size={16} color={COLORS.green} /></strong><div style={{ fontSize: '12px', color: COLORS.textGray, marginTop: '2px' }}>{answeredCount}/{items.length} statements completed</div></div><div style={{ fontSize: '12px', color: COLORS.textGray }}>All statements are retained for teaching/report-writing evidence.</div></div>
      </div>

      {CATEGORY_ORDER.map((key) => {
        const categoryItems = groupedItems[key] || []; const p = profile[key] || {}; const rs = riskStyle(p.riskBand);
        return <div key={key} style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
            <div><h2 style={{ color: COLORS.navyBlue, fontSize: '18px', margin: 0 }}>{config?.categories?.[key] || key}</h2><div style={{ fontSize: '12px', color: COLORS.textGray, marginTop: '3px' }}>{p.answered || 0}/{p.count || categoryItems.length} answered</div></div>
            <span style={{ padding: '5px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '700', color: rs.color, backgroundColor: rs.backgroundColor, border: `1px solid ${rs.border}` }}>{p.percent || 0}% - {p.riskBand || 'No risk'} (provisional)</span>
          </div>
          <div style={{ overflowX: 'auto' }}><div style={{ minWidth: '800px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(430px, 1fr) repeat(3, 115px)', gap: 0, padding: '0 8px 8px', color: COLORS.textGray, fontSize: '12px', fontWeight: '600' }}><div style={{ textAlign: 'left' }}>Statement</div><div style={{ textAlign: 'center' }}>Not at all</div><div style={{ textAlign: 'center' }}>Sometimes</div><div style={{ textAlign: 'center' }}>Often</div></div>
            {categoryItems.map((item) => <div key={item.item_code} style={{ display: 'grid', gridTemplateColumns: 'minmax(430px, 1fr) repeat(3, 115px)', alignItems: 'center', borderTop: '1px solid #E5E7EB', minHeight: '52px', padding: '0 8px' }}>
              <div style={{ fontSize: '14px', color: COLORS.textDark, padding: '8px 14px 8px 0', textAlign: 'left', lineHeight: 1.35 }}>{item.text}</div>
              {((config.response_options && config.response_options.length) ? config.response_options : RESPONSE_OPTIONS).map((option) => {
                const optionColours = { 'Not at all': '#FACC15', Sometimes: '#22C55E', Often: '#EF4444' };
                const selected = responses[item.item_code] === option;
                const colour = optionColours[option] || '#D1D5DB';
                return <button key={option} type="button" aria-label={`${option}: ${item.text}`} aria-pressed={selected} onClick={() => setResponses((prev) => ({ ...prev, [item.item_code]: option }))} style={{ justifySelf: 'center', width: '28px', height: '28px', borderRadius: '50%', border: selected ? `3px solid ${COLORS.navyBlue}` : '2px solid #9CA3AF', backgroundColor: colour, boxShadow: selected ? '0 0 0 3px rgba(0,51,102,0.12)' : 'none', cursor: 'pointer', padding: 0, color: option === 'Not at all' ? COLORS.textDark : COLORS.white, fontSize: '16px', fontWeight: '800', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{selected ? '✓' : ''}</button>;
              })}
            </div>)}
          </div></div>
        </div>;
      })}
    </>}

    <div style={styles.card}>
      <label style={styles.label}>Recommendations / next steps / notes (optional)</label>
      <textarea style={{ ...styles.input, minHeight: '90px', fontFamily: 'inherit', resize: 'vertical' }} value={notes} onChange={(e) => setNotes(e.target.value)} />
      <div style={{ marginTop: '12px', fontSize: '13px', fontWeight: '600', color: readyToSave ? COLORS.green : '#C2410C' }}>
        {readyToSave ? `All ${items.length} statements completed - ready to save.` : `${unansweredCount} statement${unansweredCount === 1 ? '' : 's'} still unanswered. You can press Submit & Lock to see which section(s) need attention.`}
      </div>
      <button style={{ ...styles.button, marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px', opacity: loading || !ageBand ? 0.6 : 1 }} onClick={submit} disabled={loading || !ageBand}><Save size={18} /> {loading ? 'Saving...' : 'Submit & Lock'}</button>
    </div>
  </div></div>;
}
